import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { flowsApi } from '../api/flows';
import { configApi } from '../api/config';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { EmptyState } from '../components/RequestState';
import type { FlowExecutionRecord, FlowLifecycleState, FlowRecord, PartnerRecord } from '../types/api';

interface FlowFormState {
  name: string;
  code: string;
  description: string;
  destinationPartnerId: string;
  autoDeliver: boolean;
}

interface ExecuteFormState {
  payload: string;
  referenceId: string;
  correlationId: string;
}

const emptyFlowForm = (): FlowFormState => ({
  name: '',
  code: '',
  description: '',
  destinationPartnerId: '',
  autoDeliver: false,
});

const emptyExecuteForm = (): ExecuteFormState => ({
  payload: '{\n  \n}',
  referenceId: '',
  correlationId: '',
});

export function FlowsPage() {
  const { search } = useLocation();
  const [flows, setFlows] = useState<FlowRecord[]>([]);
  const [partners, setPartners] = useState<PartnerRecord[]>([]);
  const [executions, setExecutions] = useState<FlowExecutionRecord[]>([]);
  const [selected, setSelected] = useState<FlowRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState<ConfirmDialogState | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [form, setForm] = useState<FlowFormState>(emptyFlowForm);
  const [saving, setSaving] = useState(false);

  // Fix 3: inline execution form state (replaces three window.prompt() calls)
  const [showExecutePanel, setShowExecutePanel] = useState(false);
  const [executeForm, setExecuteForm] = useState<ExecuteFormState>(emptyExecuteForm);
  const [executing, setExecuting] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [flowResult, executionResult, partnerResult] = await Promise.all([
        flowsApi.list({ pageSize: 50 }),
        flowsApi.executions({ pageSize: 20 }),
        configApi.partners.list({ page: 1, pageSize: 100, isActive: true }),
      ]);
      setFlows(flowResult.data);
      setExecutions(executionResult.data);
      setPartners(partnerResult.data);
      setSelected((current) => flowResult.data.find((flow) => flow.id === current?.id) ?? flowResult.data[0] ?? null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to load flows.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const selectedExecutions = useMemo(
    () => executions.filter((item) => !selected || item.flowId === selected.id),
    [executions, selected],
  );

  function requestCreateFlow() {
    setForm(emptyFlowForm());
    setFormMode('create');
  }

  function requestEditFlow() {
    if (!selected) return;
    setForm({
      name: selected.name,
      code: selected.code,
      description: selected.description ?? '',
      destinationPartnerId: selected.selectedVersion.destinationPartnerId ?? '',
      autoDeliver: selected.selectedVersion.autoDeliver,
    });
    setFormMode('edit');
  }

  async function saveFlow() {
    if (!form.name.trim() || !form.code.trim()) {
      setError('Flow name and code are required.');
      return;
    }
    if (form.autoDeliver && !form.destinationPartnerId) {
      setError('Select a destination partner before enabling automatic delivery.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      let saved: FlowRecord;
      if (formMode === 'edit' && selected) {
        const current = selected.selectedVersion;
        saved = await flowsApi.createVersion(selected.id, {
          versionLabel: nextVersionLabel(current.versionLabel, current.versionNumber),
          inboundChannel: current.inboundChannel,
          parser: current.parser,
          validator: current.validator,
          transformer: current.transformer,
          router: current.router,
          outboundConnectorId: current.outboundConnectorId,
          mappingDefinitionId: current.mappingDefinitionId,
          mappingVersionId: current.mappingVersionId,
          connectorDefinitionId: current.connectorDefinitionId,
          connectorInstanceId: current.connectorInstanceId,
          connectorVersion: current.connectorVersion,
          retryPolicy: current.retryPolicy,
          dlqPolicy: current.dlqPolicy,
          steps: current.steps,
          destinationPartnerId: form.destinationPartnerId || undefined,
          autoDeliver: form.autoDeliver,
        });
      } else {
        saved = await flowsApi.create({
          name: form.name.trim(),
          code: form.code.trim(),
          description: form.description.trim() || undefined,
          destinationPartnerId: form.destinationPartnerId || undefined,
          autoDeliver: form.autoDeliver,
          validator: { requiredFields: ['messageType'] },
          transformer: { assign: { normalizedBy: 'OSS Integration' } },
          router: { routeTo: 'default-outbound' },
        });
      }
      setSelected(saved);
      setFormMode(null);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to save flow.');
    } finally {
      setSaving(false);
    }
  }

  function transitionDisabledReason(action: 'approve' | 'activate' | 'rollback'): string | null {
    if (!selected) return 'Select a flow first.';
    const state: FlowLifecycleState = selected.selectedVersion.lifecycleState;
    if (action === 'approve' && state !== 'DRAFT') return `Cannot deploy: version is already ${state}.`;
    if (action === 'activate' && state !== 'APPROVED') return `Cannot activate: version is ${state}, expected APPROVED.`;
    if (action === 'rollback' && state !== 'ACTIVE') return `Cannot roll back: version is ${state}, expected ACTIVE.`;
    return null;
  }

  function requestTransition(action: 'approve' | 'activate' | 'rollback') {
    if (!selected || transitionDisabledReason(action)) return;
    const label = action === 'approve' ? 'Deploy' : action === 'activate' ? 'Activate' : 'Rollback';
    setDialog({
      title: `${label} flow`,
      message: `This will ${action} version ${selected.selectedVersion.versionLabel} of "${selected.name}". Current state: ${selected.selectedVersion.lifecycleState}.`,
      confirmLabel: label,
      risk: action === 'activate' || action === 'rollback' ? 'high' : 'medium',
      requireText: action === 'activate' ? 'ACTIVATE' : action === 'rollback' ? 'ROLLBACK' : undefined,
      onConfirm: () => runTransition(action),
    });
  }

  async function runTransition(action: 'approve' | 'activate' | 'rollback') {
    if (!selected) return;
    const versionId = selected.selectedVersion.id;
    const updated = action === 'approve'
      ? await flowsApi.approve(selected.id, versionId)
      : action === 'activate'
        ? await flowsApi.activate(selected.id, versionId)
        : await flowsApi.rollback(selected.id, versionId);
    setSelected(updated);
    await load();
  }

  // Fix 3: inline execution submission — no more window.prompt()
  async function submitExecution() {
    if (!selected) return;
    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(executeForm.payload) as Record<string, unknown>;
    } catch {
      setError('Execution payload must be valid JSON.');
      return;
    }
    setExecuting(true);
    setError(null);
    try {
      const execution = await flowsApi.execute(selected.id, {
        payload,
        correlationId: executeForm.correlationId.trim() || `test_${Date.now()}`,
        referenceId: executeForm.referenceId.trim() || `ref_${Date.now()}`,
      });
      setExecutions((items) => [execution, ...items]);
      setShowExecutePanel(false);
      setExecuteForm(emptyExecuteForm());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Flow execution failed.');
    } finally {
      setExecuting(false);
    }
  }

  return (
    <div className="page-stack">
      <section className="hero">
        <div>
          <span>FLOW ENGINE</span>
          <h1>Enterprise Flows</h1>
          <p>Versioned inbound, validation, transformation, routing, delivery, retry, and DLQ orchestration.</p>
        </div>
        {/* Fix 10: link to the guided builder alongside the quick-create button */}
        <div className="panel-actions">
          <Link className="button-link primary-link" to={{ pathname: '/flows/builder', search }}>Guided Flow Builder</Link>
          <button type="button" className="ghost-button" onClick={requestCreateFlow}>Advanced quick create</button>
        </div>
      </section>

      {error && <div className="error-banner">{error}</div>}
      {loading && <div className="panel">Loading flow catalog...</div>}

      {formMode ? (
        <section className="panel">
          <h2>{formMode === 'create' ? 'Create flow' : `Update ${selected?.name ?? 'flow'}`}</h2>
          {formMode === 'edit' ? <p>Saving creates a new draft version so the active version remains unchanged until approval and activation.</p> : null}
          <div className="form-grid">
            <label>
              <span>Name</span>
              <input value={form.name} disabled={formMode === 'edit'} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
            </label>
            {/* Fix 8: code no longer auto-filled with a timestamp */}
            <label>
              <span>Code</span>
              <input
                value={form.code}
                disabled={formMode === 'edit'}
                placeholder="e.g. CUSCAR_INBOUND"
                onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))}
              />
            </label>
            <label className="full-width">
              <span>Description</span>
              <input value={form.description} disabled={formMode === 'edit'} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} />
            </label>
            <label>
              <span>Destination partner</span>
              <select value={form.destinationPartnerId} onChange={(event) => setForm((current) => ({ ...current, destinationPartnerId: event.target.value }))}>
                <option value="">No destination partner</option>
                {partners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name} ({partner.code})</option>)}
              </select>
            </label>
            <label className="inline-check">
              <input
                type="checkbox"
                checked={form.autoDeliver}
                onChange={(event) => setForm((current) => ({ ...current, autoDeliver: event.target.checked }))}
              />
              Deliver automatically
            </label>
          </div>
          <div className="actions">
            <button type="button" disabled={saving} onClick={() => void saveFlow()}>
              {saving ? 'Saving...' : formMode === 'create' ? 'Create draft' : 'Create updated version'}
            </button>
            <button type="button" className="ghost-button" disabled={saving} onClick={() => setFormMode(null)}>Cancel</button>
          </div>
        </section>
      ) : null}

      <section className="grid two">
        {/* Fix 9: renamed from "Visual Flow Designer" (it was just a list table) */}
        <div className="panel">
          <h2>Flow catalog</h2>
          <div className="table">
            <div className="table-row table-head">
              <span>Flow</span>
              <span>Version</span>
              <span>State</span>
            </div>
            {flows.map((flow) => (
              <button
                type="button"
                className={`table-row table-button${selected?.id === flow.id ? ' selected' : ''}`}
                key={flow.id}
                onClick={() => { setSelected(flow); setShowExecutePanel(false); }}
              >
                <span>{flow.name}</span>
                <span>{flow.selectedVersion.versionLabel}</span>
                <span>{flow.selectedVersion.lifecycleState}</span>
              </button>
            ))}
          </div>
          {!loading && flows.length === 0 ? (
            <EmptyState label="No flows have been created yet. Use the Guided Flow Builder or Advanced quick create to add one." />
          ) : null}
        </div>

        <div className="panel">
          <h2>Deployment Manager</h2>
          {selected ? (
            <>
              <dl className="detail-list">
                <dt>Code</dt><dd>{selected.code}</dd>
                <dt>Active version</dt><dd>{selected.activeVersionId ?? 'None'}</dd>
                {/* Fix 10: mapping version links to /mapping-designer */}
                <dt>Mapping version</dt>
                <dd>
                  {selected.selectedVersion.mappingVersionId
                    ? <Link to={{ pathname: '/mapping-designer', search }}>{selected.selectedVersion.mappingVersionId}</Link>
                    : 'Not pinned'}
                </dd>
                <dt>Connector instance</dt><dd>{selected.selectedVersion.connectorInstanceId ?? 'Not attached'}</dd>
                <dt>Destination partner</dt><dd>{partnerName(partners, selected.selectedVersion.destinationPartnerId)}</dd>
                <dt>Automatic delivery</dt><dd>{selected.selectedVersion.autoDeliver ? 'Enabled' : 'Disabled'}</dd>
              </dl>
              <div className="actions">
                <button type="button" onClick={requestEditFlow}>Update flow</button>
                {(['approve', 'activate', 'rollback'] as const).map((action) => {
                  const reason = transitionDisabledReason(action);
                  const label = action === 'approve' ? 'Deploy' : action === 'activate' ? 'Activate' : 'Rollback';
                  return (
                    <button key={action} type="button" disabled={Boolean(reason)} title={reason ?? undefined} onClick={() => requestTransition(action)}>
                      {label}
                    </button>
                  );
                })}
                {/* Fix 3: opens an inline form panel instead of three window.prompt() dialogs */}
                <button type="button" onClick={() => { setShowExecutePanel((open) => !open); setError(null); }}>
                  {showExecutePanel ? 'Cancel test' : 'Execute test payload'}
                </button>
              </div>
            </>
          ) : (
            <p>No flow selected.</p>
          )}
        </div>
      </section>

      {/* Fix 3: inline execution form */}
      {showExecutePanel && selected ? (
        <section className="panel">
          <h2>Execute flow: {selected.name}</h2>
          <p>Submit a test payload against this flow version. The execution is recorded and visible in the timeline below.</p>
          <div className="form-grid">
            <label className="full-width">
              <span>JSON payload</span>
              <textarea
                rows={10}
                value={executeForm.payload}
                onChange={(event) => setExecuteForm((current) => ({ ...current, payload: event.target.value }))}
                placeholder={'{\n  "key": "value"\n}'}
              />
            </label>
            <label>
              <span>Reference ID</span>
              <input
                value={executeForm.referenceId}
                onChange={(event) => setExecuteForm((current) => ({ ...current, referenceId: event.target.value }))}
                placeholder={`ref_${Date.now()}`}
              />
            </label>
            <label>
              <span>Correlation ID</span>
              <input
                value={executeForm.correlationId}
                onChange={(event) => setExecuteForm((current) => ({ ...current, correlationId: event.target.value }))}
                placeholder={`test_${Date.now()}`}
              />
            </label>
          </div>
          <div className="actions">
            <button type="button" disabled={executing} onClick={() => void submitExecution()}>
              {executing ? 'Executing...' : 'Run execution'}
            </button>
            <button type="button" className="ghost-button" disabled={executing} onClick={() => { setShowExecutePanel(false); setExecuteForm(emptyExecuteForm()); }}>
              Cancel
            </button>
          </div>
        </section>
      ) : null}

      <ConfirmDialog state={dialog} onClose={() => setDialog(null)} />

      <section className="panel">
        <h2>Execution Timeline</h2>
        {!loading && selectedExecutions.length === 0 ? (
          <EmptyState label={selected ? `No executions recorded yet for "${selected.name}".` : 'Select a flow to view its execution timeline.'} />
        ) : null}
        {selectedExecutions.map((execution) => (
          <article className="timeline-item" key={execution.id}>
            <header>
              <strong>{execution.status}</strong>
              <span>{execution.correlationId ?? execution.id}</span>
            </header>
            <p>Flow version {execution.flowVersionNumber} · Path {execution.executionPath.join(' -> ') || 'n/a'}</p>
            <div className="step-strip">
              {execution.steps.map((step) => (
                <span key={step.id} className={`status ${step.status.toLowerCase()}`}>{step.stepName}</span>
              ))}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}

function partnerName(partners: PartnerRecord[], id?: string): string {
  if (!id) return 'Not configured';
  const partner = partners.find((candidate) => candidate.id === id);
  return partner ? `${partner.name} (${partner.code})` : id;
}

function nextVersionLabel(current: string, versionNumber: number): string {
  const match = /^v?(\d+)$/.exec(current.trim());
  return match ? `v${Number(match[1]) + 1}` : `v${versionNumber + 1}`;
}
